// src/pages/ProfileSettings.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Button,
    Form,
    FormGroup,
    Label,
    Input,
    Alert,
    Spinner,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Table,
} from 'reactstrap';
import {
    Save,
    RefreshCw,
    User,
    Mail,
    Shield,
    Key,
    CheckCircle,
    AlertCircle,
    Eye,
    EyeOff,
    Edit2,
    Lock,
    Users,
    UserCheck,
} from 'react-feather';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import { toast } from 'react-toastify';
import { apiClient } from '../../helpers/api_helper';

// Types
interface AdminUser {
    id: number;
    username?: string;
    email: string;
    role: boolean;
    isActive: boolean;
    lastLogin?: string;
    createdAt: string;
    updatedAt: string;
}

interface ChangePasswordData {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

interface UpdateProfileData {
    username?: string;
    email?: string;
}

const ProfileSettings: React.FC = () => {
    // State
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [user, setUser] = useState<AdminUser | null>(null);
    const [allUsers, setAllUsers] = useState<AdminUser[]>([]);
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);

    // Profile form
    const [profileData, setProfileData] = useState<UpdateProfileData>({
        username: '',
        email: '',
    });

    // Password form
    const [passwordData, setPasswordData] = useState<ChangePasswordData>({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });

    // Role change modal
    const [roleModal, setRoleModal] = useState(false);
    const [userToChangeRole, setUserToChangeRole] = useState<AdminUser | null>(null);
    const [newRole, setNewRole] = useState(false);

    // Validation errors
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});

    // Fetch user profile
    const fetchProfile = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const response = await apiClient.get('/users/me');
            
            if (response && response.data) {
                setUser(response.data);
                setIsAdmin(response.data.role === true);
                setProfileData({
                    username: response.data.username || '',
                    email: response.data.email || '',
                });
            }
        } catch (error: any) {
            console.error('Error fetching profile:', error);
            setError(error.message || 'Failed to fetch profile');
            toast.error('Failed to load profile');
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch all users (for admin role management)
    const fetchAllUsers = useCallback(async () => {
        try {
            const response = await apiClient.get('/users');
            if (response && response.data) {
                setAllUsers(response.data);
            }
        } catch (error: any) {
            console.error('Error fetching users:', error);
        }
    }, []);

    // Initial fetch
    useEffect(() => {
        fetchProfile();
        fetchAllUsers();
    }, [fetchProfile, fetchAllUsers]);

    // Handle profile input change
    const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setProfileData(prev => ({
            ...prev,
            [name]: value,
        }));

        if (validationErrors[name]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    // Handle password input change
    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({
            ...prev,
            [name]: value,
        }));

        if (passwordErrors[name]) {
            setPasswordErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    // Validate profile form
    const validateProfile = (): boolean => {
        const errors: Record<string, string> = {};

        if (profileData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.email)) {
            errors.email = 'Invalid email format';
        }

        setValidationErrors(errors);

        if (Object.keys(errors).length > 0) {
            toast.error(Object.values(errors)[0]);
        }

        return Object.keys(errors).length === 0;
    };

    // Validate password form
    const validatePassword = (): boolean => {
        const errors: Record<string, string> = {};

        if (!passwordData.currentPassword) {
            errors.currentPassword = 'Current password is required';
        }

        if (!passwordData.newPassword) {
            errors.newPassword = 'New password is required';
        } else if (passwordData.newPassword.length < 6) {
            errors.newPassword = 'Password must be at least 6 characters';
        }

        if (!passwordData.confirmPassword) {
            errors.confirmPassword = 'Please confirm your password';
        } else if (passwordData.newPassword !== passwordData.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }

        setPasswordErrors(errors);

        if (Object.keys(errors).length > 0) {
            toast.error(Object.values(errors)[0]);
        }

        return Object.keys(errors).length === 0;
    };

    // Handle profile update
    const handleProfileUpdate = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateProfile()) {
            return;
        }

        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const cleanData: any = { ...profileData };
            Object.keys(cleanData).forEach(key => {
                if (cleanData[key] === '' || cleanData[key] === null || cleanData[key] === undefined) {
                    delete cleanData[key];
                }
            });

            await apiClient.put('/users/me', cleanData);
            toast.success('Profile updated successfully!');
            setSuccess('Profile updated successfully!');
            await fetchProfile();
            setTimeout(() => setSuccess(''), 3000);
        } catch (error: any) {
            console.error('Error updating profile:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to update profile';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    // Handle password change
    const handlePasswordChangeSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validatePassword()) {
            return;
        }

        setSaving(true);
        setError('');
        setSuccess('');

        try {
            await apiClient.post('/users/change-password', {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword,
            });

            toast.success('Password changed successfully!');
            setSuccess('Password changed successfully!');
            setPasswordData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
            });
            setTimeout(() => setSuccess(''), 3000);
        } catch (error: any) {
            console.error('Error changing password:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to change password';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    // Handle role change
    const handleRoleChange = (user: AdminUser) => {
        setUserToChangeRole(user);
        setNewRole(!user.role);
        setRoleModal(true);
    };

    const confirmRoleChange = async () => {
        if (!userToChangeRole) return;

        try {
            // Check if trying to make another user admin
            if (newRole === true) {
                // Check if there's already an admin
                const existingAdmin = allUsers.find(u => u.role === true && u.id !== userToChangeRole.id);
                if (existingAdmin) {
                    toast.error('Cannot make this user admin. There can only be one admin user.');
                    setRoleModal(false);
                    return;
                }
            }

            await apiClient.patch(`/users/${userToChangeRole.id}/role`, {
                role: newRole,
            });

            toast.success(`User role updated successfully!`);
            setRoleModal(false);
            await fetchAllUsers();
            await fetchProfile();
        } catch (error: any) {
            console.error('Error updating role:', error);
            toast.error(error.response?.data?.message || 'Failed to update role');
        }
    };

    // Get role badge
    const getRoleBadge = (role: boolean) => {
        return role ? (
            <Badge color="danger" className="ms-2">
                <Shield size={12} className="me-1" />
                Admin
            </Badge>
        ) : (
            <Badge color="secondary" className="ms-2">
                <User size={12} className="me-1" />
                User
            </Badge>
        );
    };

    // Get status badge
    const getStatusBadge = (isActive: boolean) => {
        return isActive ? (
            <Badge color="success">Active</Badge>
        ) : (
            <Badge color="warning">Inactive</Badge>
        );
    };

    // Loading state
    if (loading) {
        return (
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Profile Settings" pageTitle="Settings" />
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardBody className="text-center py-5">
                                    <Spinner color="primary" className="me-2" />
                                    <span>Loading profile...</span>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb title="Admin Users" pageTitle="Settings" />

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div>
                                    <h5 className="card-title mb-0">Admin Users</h5>
                                    {user && (
                                        <small className="text-muted">
                                            {user.email} • {getRoleBadge(user.role)}
                                        </small>
                                    )}
                                </div>
                                <div className="d-flex gap-2">
                                    <Button
                                        color="secondary"
                                        size="sm"
                                        onClick={() => {
                                            fetchProfile();
                                            fetchAllUsers();
                                            toast.info('Admin users reloaded');
                                        }}
                                        disabled={saving}
                                    >
                                        <RefreshCw size={14} className="me-1" />
                                        Refresh
                                    </Button>
                                </div>
                            </CardHeader>

                            <CardBody>
                                {/* Alerts */}
                                {error && (
                                    <Alert color="danger" className="mb-3">
                                        <AlertCircle size={16} className="me-2" />
                                        {error}
                                    </Alert>
                                )}

                                {success && (
                                    <Alert color="success" className="mb-3">
                                        <CheckCircle size={16} className="me-2" />
                                        {success}
                                    </Alert>
                                )}

                                <Row>
                                    {/* Left Column - Profile Info */}
                                    <Col lg={6}>
                                        <div className="mb-4">
                                            <h6 className="text-primary mb-3">
                                                <User size={16} className="me-2" />
                                                Personal Information
                                            </h6>
                                            <Form onSubmit={handleProfileUpdate}>
                                                <div className="table-responsive">
                                                    <Table bordered className="mb-0">
                                                        <tbody>
                                                            <tr>
                                                                <td className="bg-light" style={{ width: '150px', fontWeight: '600' }}>
                                                                    <User size={14} className="me-1" />
                                                                    Username
                                                                </td>
                                                                <td>
                                                                    <Input
                                                                        type="text"
                                                                        name="username"
                                                                        value={profileData.username || ''}
                                                                        onChange={handleProfileChange}
                                                                        bsSize="sm"
                                                                        placeholder="Enter username"
                                                                    />
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td className="bg-light" style={{ fontWeight: '600' }}>
                                                                    <Mail size={14} className="me-1" />
                                                                    Email
                                                                </td>
                                                                <td>
                                                                    <Input
                                                                        type="email"
                                                                        name="email"
                                                                        value={profileData.email || ''}
                                                                        onChange={handleProfileChange}
                                                                        invalid={!!validationErrors.email}
                                                                        bsSize="sm"
                                                                        placeholder="Enter email"
                                                                    />
                                                                    {validationErrors.email && (
                                                                        <div className="text-danger small mt-1">{validationErrors.email}</div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td className="bg-light" style={{ fontWeight: '600' }}>
                                                                    <Shield size={14} className="me-1" />
                                                                    Role
                                                                </td>
                                                                <td>
                                                                    {getRoleBadge(user?.role || false)}
                                                                    <span className="text-muted ms-2">
                                                                        {user?.role ? '(Admin)' : '(User)'}
                                                                    </span>
                                                                    {isAdmin && (
                                                                        <small className="text-muted d-block mt-1">
                                                                            Only one admin user is allowed
                                                                        </small>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td className="bg-light" style={{ fontWeight: '600' }}>
                                                                    <UserCheck size={14} className="me-1" />
                                                                    Status
                                                                </td>
                                                                <td>
                                                                    {getStatusBadge(user?.isActive || false)}
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </Table>
                                                </div>

                                                <div className="mt-3 d-flex justify-content-end">
                                                    <Button
                                                        color="primary"
                                                        type="submit"
                                                        size="sm"
                                                        disabled={saving}
                                                    >
                                                        {saving ? (
                                                            <>
                                                                <Spinner size="sm" className="me-2" />
                                                                Saving...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Save size={14} className="me-1" />
                                                                Update Profile
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            </Form>
                                        </div>
                                    </Col>

                                    {/* Right Column - Change Password */}
                                    <Col lg={6}>
                                        <div className="mb-4">
                                            <h6 className="text-primary mb-3">
                                                <Lock size={16} className="me-2" />
                                                Change Password
                                            </h6>
                                            <Form onSubmit={handlePasswordChangeSubmit}>
                                                <div className="table-responsive">
                                                    <Table bordered className="mb-0">
                                                        <tbody>
                                                            <tr>
                                                                <td className="bg-light" style={{ width: '150px', fontWeight: '600' }}>
                                                                    <Key size={14} className="me-1" />
                                                                    Current Password
                                                                </td>
                                                                <td>
                                                                    <div className="position-relative">
                                                                        <Input
                                                                            type={showPassword ? "text" : "password"}
                                                                            name="currentPassword"
                                                                            value={passwordData.currentPassword}
                                                                            onChange={handlePasswordChange}
                                                                            invalid={!!passwordErrors.currentPassword}
                                                                            bsSize="sm"
                                                                            placeholder="Enter current password"
                                                                        />
                                                                        <Button
                                                                            type="button"
                                                                            color="link"
                                                                            className="position-absolute end-0 top-0 text-decoration-none text-muted"
                                                                            onClick={() => setShowPassword(!showPassword)}
                                                                            style={{ padding: '6px 10px' }}
                                                                        >
                                                                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                                                        </Button>
                                                                    </div>
                                                                    {passwordErrors.currentPassword && (
                                                                        <div className="text-danger small mt-1">{passwordErrors.currentPassword}</div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td className="bg-light" style={{ fontWeight: '600' }}>
                                                                    <Key size={14} className="me-1" />
                                                                    New Password
                                                                </td>
                                                                <td>
                                                                    <div className="position-relative">
                                                                        <Input
                                                                            type={showNewPassword ? "text" : "password"}
                                                                            name="newPassword"
                                                                            value={passwordData.newPassword}
                                                                            onChange={handlePasswordChange}
                                                                            invalid={!!passwordErrors.newPassword}
                                                                            bsSize="sm"
                                                                            placeholder="Enter new password (min 6 chars)"
                                                                        />
                                                                        <Button
                                                                            type="button"
                                                                            color="link"
                                                                            className="position-absolute end-0 top-0 text-decoration-none text-muted"
                                                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                                                            style={{ padding: '6px 10px' }}
                                                                        >
                                                                            {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                                                        </Button>
                                                                    </div>
                                                                    {passwordErrors.newPassword && (
                                                                        <div className="text-danger small mt-1">{passwordErrors.newPassword}</div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td className="bg-light" style={{ fontWeight: '600' }}>
                                                                    <Key size={14} className="me-1" />
                                                                    Confirm Password
                                                                </td>
                                                                <td>
                                                                    <div className="position-relative">
                                                                        <Input
                                                                            type={showConfirmPassword ? "text" : "password"}
                                                                            name="confirmPassword"
                                                                            value={passwordData.confirmPassword}
                                                                            onChange={handlePasswordChange}
                                                                            invalid={!!passwordErrors.confirmPassword}
                                                                            bsSize="sm"
                                                                            placeholder="Confirm new password"
                                                                        />
                                                                        <Button
                                                                            type="button"
                                                                            color="link"
                                                                            className="position-absolute end-0 top-0 text-decoration-none text-muted"
                                                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                                            style={{ padding: '6px 10px' }}
                                                                        >
                                                                            {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                                                        </Button>
                                                                    </div>
                                                                    {passwordErrors.confirmPassword && (
                                                                        <div className="text-danger small mt-1">{passwordErrors.confirmPassword}</div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </Table>
                                                </div>

                                                <div className="mt-3 d-flex justify-content-end">
                                                    <Button
                                                        color="primary"
                                                        type="submit"
                                                        size="sm"
                                                        disabled={saving}
                                                    >
                                                        {saving ? (
                                                            <>
                                                                <Spinner size="sm" className="me-2" />
                                                                Changing...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Key size={14} className="me-1" />
                                                                Change Password
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            </Form>
                                        </div>
                                    </Col>
                                </Row>

                                {/* Admin Users Section (Only for Admins) */}
                                {isAdmin && allUsers.length > 0 && (
                                    <div className="mt-4">
                                        <h6 className="text-primary mb-3">
                                            <Users size={16} className="me-2" />
                                            User Management
                                        </h6>
                                        <div className="table-responsive">
                                            <Table bordered striped hover className="mb-0">
                                                <thead className="bg-light">
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Username</th>
                                                        <th>Email</th>
                                                        <th>Role</th>
                                                        <th>Status</th>
                                                        <th>Last Login</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {allUsers.map((u, index) => (
                                                        <tr key={u.id}>
                                                            <td>{index + 1}</td>
                                                            <td>{u.username || '—'}</td>
                                                            <td>{u.email}</td>
                                                            <td>{getRoleBadge(u.role)}</td>
                                                            <td>{getStatusBadge(u.isActive)}</td>
                                                            <td>
                                                                {u.lastLogin ? new Date(u.lastLogin).toLocaleString() : 'Never'}
                                                            </td>
                                                            <td>
                                                                {user?.id !== u.id && (
                                                                    <Button
                                                                        color={u.role ? 'warning' : 'danger'}
                                                                        size="sm"
                                                                        onClick={() => handleRoleChange(u)}
                                                                        title={u.role ? 'Remove Admin' : 'Make Admin'}
                                                                        disabled={u.role === false && allUsers.some(admin => admin.role === true)}
                                                                    >
                                                                        <Shield size={14} className="me-1" />
                                                                        {u.role ? 'Remove Admin' : 'Make Admin'}
                                                                    </Button>
                                                                )}
                                                                {user?.id === u.id && (
                                                                    <Badge color="info">You</Badge>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                        {allUsers.some(admin => admin.role === true) && (
                                            <small className="text-muted">
                                                <Shield size={12} className="me-1" />
                                                Only one admin user is allowed in the system
                                            </small>
                                        )}
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Role Change Confirmation Modal */}
                <Modal isOpen={roleModal} toggle={() => setRoleModal(false)}>
                    <ModalHeader toggle={() => setRoleModal(false)}>
                        {newRole ? 'Make Admin' : 'Remove Admin'}
                    </ModalHeader>
                    <ModalBody>
                        <p>
                            Are you sure you want to <strong>{newRole ? 'make' : 'remove'}</strong>{' '}
                            <strong>{userToChangeRole?.username || userToChangeRole?.email}</strong>{' '}
                            {newRole ? 'an admin' : 'from admin'}?
                        </p>
                        {newRole && allUsers.some(u => u.role === true) && (
                            <Alert color="warning" className="mt-3">
                                <AlertCircle size={16} className="me-2" />
                                <strong>Warning:</strong> There is already an admin user. Only one admin is allowed.
                                The current admin will be downgraded to a regular user.
                            </Alert>
                        )}
                        {!newRole && (
                            <Alert color="warning" className="mt-3">
                                <AlertCircle size={16} className="me-2" />
                                This user will lose admin privileges and become a regular user.
                            </Alert>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="light" onClick={() => setRoleModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            color={newRole ? 'danger' : 'warning'}
                            onClick={confirmRoleChange}
                        >
                            {newRole ? 'Make Admin' : 'Remove Admin'}
                        </Button>
                    </ModalFooter>
                </Modal>
            </Container>
        </div>
    );
};

export default ProfileSettings;